import React, { useState } from 'react';
import { Currency, formatMoney } from '../../common/format';
import { RequestResponseViewer } from '../../common/request-response-viewer';
import {
  COMMON_MONTHLY_PROJECTION_COLUMNS,
  COMMON_PROJECTION_COLUMNS,
  ChartMarker,
  ChartSeriesConfig,
  CurrencyHeader,
  EvolutionChart,
  KpiCard,
  KpiGrid,
  ProjectionTable,
  SimulationWarnings,
  buildEvolutionSeries,
} from '../_shared';
import { LifeMaxResponse } from './service';

const RAS_MARKER_COLOR = 'var(--bs-danger)';

interface RasTriggerCtx {
  year?: number;
  month?: number;
  month_index?: number;
  ras_value?: number;
  ras_min?: number;
}

function getRasTrigger(
  warnings: LifeMaxResponse['warnings'],
): RasTriggerCtx | null {
  const w = warnings?.find((x) => x.issue_type === 'ras_warning');
  if (!w?.ctx) return null;
  return w.ctx as RasTriggerCtx;
}

function buildRasMarkerLabel(ctx: RasTriggerCtx): string {
  if (typeof ctx.ras_value === 'number') {
    return `RAS ${(ctx.ras_value * 100).toFixed(2)}%`;
  }
  return 'RAS bajo mínimo';
}

const CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
  {
    dataKey: 'death_compensation',
    name: 'Indemnización fallecimiento',
    stroke: 'var(--bs-success)',
    strokeDasharray: '5 4',
  },
];

const MONTHLY_CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
];

interface LifeMaxResultViewProps {
  request: unknown;
  response?: LifeMaxResponse;
  error?: unknown;
}

export function LifeMaxResultView({
  request,
  response,
  error,
}: LifeMaxResultViewProps) {
  const [currency, setCurrency] = useState<Currency>('UF');
  const hasSuccess = !!response && !error;

  const rasTrigger = hasSuccess && response ? getRasTrigger(response.warnings) : null;

  const annualRasMarkers: ChartMarker[] = rasTrigger && typeof rasTrigger.year === 'number'
    ? [{
      xValue: rasTrigger.year,
      yDataKey: 'policy_value',
      label: buildRasMarkerLabel(rasTrigger),
      color: RAS_MARKER_COLOR,
    }]
    : [];

  const monthlyRasMarkers: ChartMarker[] = rasTrigger && typeof rasTrigger.month_index === 'number'
    ? [{
      xValue: rasTrigger.month_index + 1,
      yDataKey: 'policy_value',
      label: buildRasMarkerLabel(rasTrigger),
      color: RAS_MARKER_COLOR,
    }]
    : [];

  return (
    <div className="mt-4">
      {hasSuccess && response && (
        <>
          <CurrencyHeader
            ufValue={response.uf_value}
            currency={currency}
            onCurrencyChange={setCurrency}
          />

          <SimulationWarnings warnings={response.warnings} />

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Prima única"
                value={formatMoney(response.initial_contribution.total, currency, response.uf_value)}
                accent="text-primary"
              />
              <KpiCard
                label="Prima básica"
                value={formatMoney(response.initial_contribution.basic, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima excedente"
                value={formatMoney(response.initial_contribution.excess, currency, response.uf_value)}
              />
              <KpiCard
                label="Ahorro bruto"
                value={formatMoney(response.savings.gross, currency, response.uf_value)}
                hint={`${response.timeframe_months} meses`}
                accent="text-success"
              />
              <KpiCard
                label="Interés ganado"
                value={formatMoney(response.savings.interest, currency, response.uf_value)}
              />
              <KpiCard
                label="Capital aportado neto"
                value={formatMoney(response.savings.net, currency, response.uf_value)}
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <EvolutionChart
              data={buildEvolutionSeries(
                response.projection_by_year,
                ['policy_value', 'death_compensation'],
                currency,
                response.uf_value,
              )}
              series={CHART_SERIES}
              currency={currency}
              markers={annualRasMarkers}
            />
          </div>

          {response.projection_by_month && (
            <>
              <div className="mb-4">
                <EvolutionChart
                  title="Evolución mensual de la póliza"
                  data={buildEvolutionSeries(
                    response.projection_by_month,
                    ['policy_value'],
                    currency,
                    response.uf_value,
                    ['month'],
                  )}
                  series={MONTHLY_CHART_SERIES}
                  currency={currency}
                  xKey="month"
                  xLabel="Mes"
                  markers={monthlyRasMarkers}
                />
              </div>

              <div className="mb-4">
                <ProjectionTable
                  rows={response.projection_by_month}
                  columns={COMMON_MONTHLY_PROJECTION_COLUMNS}
                  currency={currency}
                  ufValue={response.uf_value}
                  title="Detalle mensual"
                  rowKey="month"
                />
              </div>
            </>
          )}

          <div className="mb-4">
            <ProjectionTable
              rows={response.projection_by_year}
              columns={COMMON_PROJECTION_COLUMNS}
              currency={currency}
              ufValue={response.uf_value}
            />
          </div>
        </>
      )}

      <RequestResponseViewer request={request} response={response} error={error} />
    </div>
  );
}
